import { ErrorBoundary, type FallbackRender } from '@sentry/react';
import { Suspense, useState } from 'react';

import { AlertTriangle, ChevronRight } from 'lucide-react';
import { cn } from '../../utils/cn';
import { RingSpinner } from '../loader/ring-spinner';
import { MotionDiv } from '../motion-div';
import { Card } from '../shadcn/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../shadcn/collapsible';

export function AnimatedSuspense({
  children,
  loader,
  fallback,
  className,
}: {
  children: React.ReactNode;
  loader?: React.ReactNode;
  fallback?: FallbackRender;
  className?: string;
}) {
  return (
    <ErrorBoundary
      fallback={(e) =>
        fallback ? (
          <MotionDiv className={className}>{fallback(e)}</MotionDiv>
        ) : (
          <MotionDiv className={className}>
            <DefaultFallback error={e.error} className={className} />
          </MotionDiv>
        )
      }
    >
      <Suspense fallback={<MotionDiv className={className}>{loader ?? <RingSpinner />}</MotionDiv>}>
        <MotionDiv className={className}>{children}</MotionDiv>
      </Suspense>
    </ErrorBoundary>
  );
}

export const DefaultFallback = ({ error, className }: { error: unknown; className?: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Card className={cn('flex h-full w-full flex-col gap-3 p-4', className)}>
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-gray-400" />
        <h1 className="text-sm font-medium text-gray-600">Error occured</h1>
      </div>
      <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full flex-col items-center">
        <CollapsibleTrigger className="flex cursor-pointer items-center gap-1 text-xs text-gray-600">
          <ChevronRight
            className={cn(
              'size-3 text-gray-400 transition-transform duration-200',
              isOpen && 'rotate-90',
            )}
          />
          Details
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <p className="text-xs break-all text-gray-500">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};
